import { SentenceRecordingType } from 'types/types'
import { computed, getCurrentScope, onScopeDispose, reactive, watch } from 'vue'
import type { VueLogger } from 'vue-logger-plugin'

import { StyledRecordings } from '@/composables/styledRecording'
import { RECORDING_REFETCH_INTERVAL_MS } from '@/constants/practiceConstants'
import store from '@/store'
import { SentenceRecordingMetadataWithPitchedAudios } from '@/util/AudioUtil'
import { isProcessing } from '@/util/RecordingHelpers'

/** The melodies a recording can be sung to. */
export type RecordingMelodies = Partial<Pick<SentenceRecordingType, 'haftarahMelody' | 'sentenceGroupMelody' | 'torahMelody'>>

interface RecordingManagementState {
  refetchIntervals: Record<string, NodeJS.Timeout>
}

export function useRecordingManagement(logger?: VueLogger) {
  const state = reactive<RecordingManagementState>({
    refetchIntervals: {},
  })

  // Pure computed — no side effects
  const sentenceRecordingMetadatas = computed<StyledRecordings>(() => {
    try {
      const result: StyledRecordings = {}
      for (const sentenceId of store.getters.audio.recordedSentenceIds) {
        result[sentenceId] = store.getters.audio.getRecordingListForSentence(sentenceId)
      }
      return result
    } catch (e) {
      console.error('useRecordingManagement.sentenceRecordingMetadatas ERROR:', e)
      return {} as StyledRecordings
    }
  })

  // Watch store recordings with deep:true so nested property changes
  // (e.g. processingEndedAt being set) trigger the watcher.
  // The sentenceRecordingMetadatas computed doesn't access .sentenceRecording
  // fields, so watching it alone misses those mutations.
  watch(
    () => store.getters.audio.recordings,
    () => {
      const metadatas = sentenceRecordingMetadatas.value
      const activeRecordingIds = new Set<string>()
      for (const recordings of Object.values(metadatas)) {
        for (const metadata of recordings) {
          const recordingId = metadata.sentenceRecording?.id
          if (!recordingId) {
            continue
          }
          if (isProcessing(metadata)) {
            activeRecordingIds.add(recordingId)
            if (!state.refetchIntervals[recordingId]) {
              console.log(`[polling] starting interval for recording ${recordingId}`)
              state.refetchIntervals[recordingId] = setInterval(() => {
                store.dispatch.audio.fetchRecording({ id: parseInt(recordingId, 10) }).catch((err) => {
                  console.error(`[polling] fetchRecording ${recordingId} failed:`, err)
                  clearAndDeleteRefetch(recordingId)
                })
              }, RECORDING_REFETCH_INTERVAL_MS)
            }
          }
        }
      }
      // Clear intervals for recordings no longer processing
      for (const recordingId of Object.keys(state.refetchIntervals)) {
        if (!activeRecordingIds.has(recordingId)) {
          console.log(`[polling] stopped for recording ${recordingId} (processing complete)`)
          clearAndDeleteRefetch(recordingId)
        }
      }
    },
    { deep: true, immediate: true },
  )

  // Methods
  const clearAndDeleteRefetch = (sentenceRecordingId: string) => {
    if (state.refetchIntervals[sentenceRecordingId]) {
      clearInterval(state.refetchIntervals[sentenceRecordingId])
      delete state.refetchIntervals[sentenceRecordingId]
    }
  }

  /** Delete a recording. Resolves false (the store already told the user why) if it could not be deleted. */
  const confirmDeleteRecording = async (sentenceRecordingMetadata: SentenceRecordingMetadataWithPitchedAudios): Promise<boolean> => {
    try {
      logger?.debug('Confirming delete recording', { sentenceRecordingMetadata })

      if (!sentenceRecordingMetadata.sentenceRecording) {
        throw new Error('No sentence recording found')
      }

      const deleted = await store.dispatch.audio.deleteRecording({
        sentenceRecording: sentenceRecordingMetadata.sentenceRecording,
      })
      if (deleted) {
        clearAndDeleteRefetch(sentenceRecordingMetadata.sentenceRecording.id)
        logger?.info('Recording deleted successfully')
      }
      return deleted
    } catch (error) {
      logger?.error('Error deleting recording', { error })
      throw error
    }
  }

  const fetchRecordingForDownload = async (sentenceRecordingMetadata: SentenceRecordingMetadataWithPitchedAudios) => {
    try {
      logger?.debug('Fetching recording for download', { sentenceRecordingMetadata })

      if (!sentenceRecordingMetadata.sentenceRecording) {
        throw new Error('No sentence recording found')
      }

      // This would typically fetch the audio file URL or blob
      return await store.dispatch.audio.fetchAudio({
        cache: true,
        pitchShift: '0',
        sentenceRecording: sentenceRecordingMetadata.sentenceRecording,
      })
    } catch (error) {
      logger?.error('Error fetching recording for download', { error })
      throw error
    }
  }

  /** Save a recording's metadata. Resolves false (the store already told the user why) if it could not be saved. */
  const updateSentenceRecordingMetadata = async (
    sentenceRecordingMetadata: SentenceRecordingMetadataWithPitchedAudios,
  ): Promise<boolean> => {
    try {
      logger?.debug('Updating sentence recording metadata', { sentenceRecordingMetadata })

      const updated = await store.dispatch.audio.modifySentenceRecording({
        sentenceRecordingMetadataWithPitchedAudios: sentenceRecordingMetadata,
      })
      if (updated) {
        logger?.info('Recording metadata updated successfully')
      }
      return updated
    } catch (error) {
      logger?.error('Error updating recording metadata', { error })
      throw error
    }
  }

  /** Change a recording's melodies and save them. If saving fails the recording keeps its previous melodies. */
  const updateRecordingMelodies = async (
    sentenceRecordingMetadata: SentenceRecordingMetadataWithPitchedAudios,
    melodies: RecordingMelodies,
  ): Promise<boolean> => {
    const { sentenceRecording } = sentenceRecordingMetadata
    if (!sentenceRecording) {
      return false
    }
    const { haftarahMelody, sentenceGroupMelody, torahMelody } = sentenceRecording
    Object.assign(sentenceRecording, melodies)
    const updated = await updateSentenceRecordingMetadata(sentenceRecordingMetadata)
    if (!updated) {
      Object.assign(sentenceRecording, { haftarahMelody, sentenceGroupMelody, torahMelody })
    }
    return updated
  }

  const clearAllRefetchIntervals = () => {
    Object.keys(state.refetchIntervals).forEach((key) => {
      clearAndDeleteRefetch(key)
    })
    logger?.debug('Cleared all refetch intervals')
  }

  // Clean up all polling intervals when the composable's scope is disposed
  // (e.g., when the component using this composable unmounts)
  if (getCurrentScope()) {
    onScopeDispose(clearAllRefetchIntervals)
  }

  return {
    clearAllRefetchIntervals,

    confirmDeleteRecording,

    fetchRecordingForDownload,
    // Computed
    sentenceRecordingMetadatas,
    updateRecordingMelodies,
    updateSentenceRecordingMetadata,
  }
}
